import React from 'react'
import xss from 'xss'
import XssWhiteList from '../common/XssWhiteList'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardActionArea from '@material-ui/core/CardActionArea'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Avatar from '@material-ui/core/Avatar'
import Typography from '@material-ui/core/Typography'
import Wrapper from '../common/Wrapper'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '8px'
  },
  card: {
    maxWidth: 344,
    border: '1px solid rgba(0, 44, 56, 0.12)',
    boxSizing: 'border-box',
    borderRadius: '4px',
    margin: 'auto',
    '@media (min-width: 1024px)': {
      maxWidth: 400
    }
  },
  setMinHeight: {
    minHeight: 80
  },
  cardHeaderStyles: {
    padding: '16px 16px 0 16px'
  },
  headerContent: {
    alignSelf: 'flex-start'
  },
  overline: {
    lineHeight: 0.75
  },
  avatar: {
    height: '40px',
    width: '40px',
    backgroundColor: theme.palette.secondary.main
  },
  user: {
    color: theme.palette.secondary.main,
    textTransform: 'uppercase'
  },
  date: {
    color: theme.palette.text.secondary,
    textTransform: 'none'
  },
  subtitle: {
    textAlign: 'left',
    color: theme.palette.text.secondary,
    textTransform: 'none',
    lineHeight: 2
  },
  textContent: {
    padding: '0px 16px 16px 70px'
  },
  noSubtitle: {
    marginTop: '-16px'
  },
  noCardHeaderContent: {
    marginTop: '-40px'
  },
  text: {
    lineHeight: '21px',
    color: theme.palette.text.primary
  }
}))

export interface Props {
  /**
   * Image for avatar
   */
  avatarImage?: string
  /**
   * Text for avatar
   */
  avatarText?: string
  /**
   * What avatar color to use
   */
  avatarColor?: string
  /**
   * What avatar bacground color to use
   */
  avatarBackgroundColor?: string
  /**
   * User name on feed card
   */
  user?: string
  /**
   * Date displayed next to the user
   */
  date?: string
  /**
   * Subtitle displayed under the user and the date
   */
  subtitle?: string
  /**
   * Feedcard's text
   */
  text?: string
  /**
   * Style attribute
   */
  style?: React.CSSProperties
  /**
   * Class attribute
   */
  className?: string
  /**
   * Determine the max-width of the component.
   * The component width grows with the size of the screen.
   * Set to `false` to disable `maxWidth`.
   */
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false
}

const FeedCard: React.FC<Props> = ({
  avatarImage,
  avatarText,
  avatarColor,
  avatarBackgroundColor,
  user,
  date,
  subtitle,
  text,
  style,
  className,
  maxWidth
}) => {
  const classes = useStyles()

  return (
    <Wrapper style={style} className={className} maxWidth={maxWidth}>
      <div className={classes.container}>
        <Card
          classes={{ root: classes.card }}
          className={clsx({ [classes.setMinHeight]: !text })}
        >
          <CardActionArea>
            <CardHeader
              classes={{
                root: classes.cardHeaderStyles,
                content: classes.headerContent
              }}
              avatar={
                avatarImage ? (
                  <Avatar alt="Person" src={avatarImage} />
                ) : (
                  <Avatar
                    aria-label="avatar"
                    className={classes.avatar}
                    style={{
                      color: avatarColor,
                      backgroundColor: avatarBackgroundColor
                    }}
                  >
                    {avatarText}
                  </Avatar>
                )
              }
              title={
                <div style={{ display: 'flex' }}>
                  <Typography
                    className={clsx(classes.user, classes.overline)}
                    style={{
                      marginRight: user ? '8px' : '0px',
                      color: avatarBackgroundColor
                    }}
                    variant="overline"
                  >
                    {user}
                  </Typography>
                  {date ? (
                    <Typography
                      className={clsx(classes.date, classes.overline)}
                      variant="overline"
                    >
                      {date}
                    </Typography>
                  ) : null}
                </div>
              }
              subheader={
                <Typography
                  variant="overline"
                  className={clsx(classes.subtitle, classes.overline)}
                >
                  {subtitle}
                </Typography>
              }
            />
            <CardContent
              classes={{
                root: clsx(classes.textContent, {
                  [classes.noSubtitle]: !subtitle,
                  [classes.noCardHeaderContent]: !user && !date && !subtitle
                })
              }}
            >
              {text ? (
                <Typography
                  variant="body2"
                  className={classes.text}
                  dangerouslySetInnerHTML={{
                    __html: xss(text, XssWhiteList)
                  }}
                />
              ) : null}
            </CardContent>
          </CardActionArea>
        </Card>
      </div>
    </Wrapper>
  )
}

export default FeedCard
